// The challenge a share link opened the practice screen with: the card's data,
// the sharer's recording, and the session copy that survives a refresh.

import { ref } from 'vue';
import { parseShareParams, hasShareParams } from '../services/share';
import { fetchSharedAudioUrl } from '../services/shareVoice';

const STORAGE_KEY = 'ezshadowChallenge.v1';

/**
 * Falls back to sessionStorage because the screen strips everything but v and
 * t from the URL once the first sentence resolves; a refresh would lose it.
 */
export function loadChallenge(query) {
  // The app writes ?v=&t= itself, so only real share params count, or an
  // ordinary visit would skip onboarding as a "challenge".
  const fromUrl = hasShareParams(query) ? parseShareParams(query) : null;
  if (fromUrl) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl));
    } catch (e) { /* storage unavailable */ }
    return fromUrl;
  }
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    // Same video only, and validate the shape so an entry from an older
    // bundle can't resurrect an empty challenge card.
    const meaningful = saved && (saved.score !== null || saved.name ||
      saved.sentence || saved.audioId || saved.lang || saved.motherTongue);
    if (meaningful && saved.videoId && saved.videoId === query.v) return saved;
  } catch (e) { /* corrupt -> no challenge */ }
  return null;
}

export function forgetChallenge() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) { /* storage unavailable */ }
}

/**
 * The sharer's language pair, resolved against the app's languages, so a
 * recipient isn't dropped on the defaults. Null when neither side resolves.
 */
export function challengeLanguages(challenge, available, browserLang) {
  const byCode = code => {
    if (!code) return null;
    const base = String(code).toLowerCase().split('-')[0];
    return available.filter(l => l.code === base)[0] || null;
  };

  const target = byCode(challenge.lang);
  const mother = byCode(challenge.motherTongue) || byCode(browserLang);

  if (!target && !mother) return null;
  return {
    targetLanguage: target || undefined,
    // Same code on both sides would translate a language into itself.
    sourceLanguage: mother && (!target || mother.code !== target.code) ? mother : undefined
  };
}

export function useChallenge() {
  const challenge = ref(null);
  const playingChallenge = ref(false);
  let audioUrl = null;

  function releaseChallengeAudio() {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      audioUrl = null;
    }
  }

  function clearChallenge() {
    if (!challenge.value) return;
    challenge.value = null;
    releaseChallengeAudio();
    forgetChallenge();
  }

  // Routed through playSelf to inherit the iOS speaker routing.
  async function hearChallenge(playSelf) {
    if (!challenge.value || !challenge.value.audioId) return;
    try {
      if (!audioUrl) {
        audioUrl = await fetchSharedAudioUrl(challenge.value.audioId);
      }
      playingChallenge.value = true;
      playSelf(audioUrl);
      // Only the challenge button's label; playSelf drives "Hear yourself".
      setTimeout(() => { playingChallenge.value = false; }, 15000);
    } catch (e) {
      playingChallenge.value = false;
      // Expired/revoked recording: the challenge still works without it.
      challenge.value = Object.assign({}, challenge.value, { audioId: null });
    }
  }

  // Adopt the sharer's language pair silently.
  async function applyChallengeLanguages(store) {
    const langs = challengeLanguages(challenge.value, store.state.settings.availableLanguages,
      typeof navigator !== 'undefined' ? navigator.language : '');
    if (!langs) return;
    await store.dispatch('settings/updateLanguages', langs);
    // Persist, or the first-run modal (keyed on saved languages) reappears.
    await store.dispatch('settings/saveSettings');
  }

  return { challenge, playingChallenge, clearChallenge, hearChallenge, releaseChallengeAudio, applyChallengeLanguages };
}
